import { clamp } from '@/core/rng';
import { FOCUS_BANDS } from '@/sim/simulation';

/**
 * The deity's presence — an orbit camera over the island (2026-08-17), whose
 * drag turns the head (2026-08-18).
 *
 * The presence is an EYE that stands off a POINT ON THE GROUND and looks at
 * it: a target, a distance and two angles. **The eye is where the player is.**
 * A left drag turns the gaze from where the eye stands and the target is
 * re-found where the gaze meets the ground (`lookAround`) — looking around,
 * not swinging about a point on the land; the wheel brings the eye closer to
 * what it looks at or further off; W A S D (or a right-button drag) carry the
 * player across the land; Space and C raise and lower the eye over what it
 * watches; the mouse is a free cursor, and whatever it is over is what the god
 * means. The captured mouse of the day before — the pointer welded to the
 * centre of the frame, looking with no button held — is still gone: a god
 * watching a valley wants a hand that can point at one thing in it while the
 * eye stays where it is, and a drag that turns the head gives it both.
 *
 * ## The eye is still somewhere
 *
 * Everything downstream reads `x y z` (the eye), `yaw pitch` (its gaze) and
 * `vx vy vz` exactly as before — LOD, streaming, fog, awareness, the cost of a
 * distant cast, the arrival's flight, the follow — and this class derives them
 * every frame from the orbit. Nothing here touches the simulation; this is the
 * player's body, and the sim never learns where it is.
 *
 * ## Distance is the scale of attention again
 *
 * `main.ts` reads Divine Focus off `r` through `focusFor` — the band table the
 * old camera paid at each distance and the one the wheel walked as a bare
 * number since. Close, a cast is channelled into the thing under the cursor;
 * zoom out and the same click means the party, then the band, then the country
 * (`ui/thread.ts`'s registers). Reach caps `r` (`maxClearance`), so a local
 * god's widest attention is still what its old ceiling permitted.
 *
 * ## Moving lets go
 *
 * A chosen subject holds the target (`hold`) and the eye tracks it. The
 * moment the hand moves the target itself — a pan key, a pan drag — the hold
 * is dropped by whoever is holding it (`main.ts` reads `moving`); orbiting and
 * zooming are not moving, so a god may walk round a herd and lean in without
 * losing it. The subject stays chosen and its card stays open: deciding to
 * look elsewhere is not deciding to stop caring.
 */

/** What the keys are asking for. Driven by the listeners when wired; by tests when not. */
export interface FlightInput {
  /** −1..1 across the ground away from the eye (W/S). */
  forward: number;
  /** −1..1 across the ground to the eye's right (D/A). */
  strafe: number;
  /** −1..1 raising and lowering the eye itself (Space up; C or Ctrl down). */
  lift: number;
  surge: boolean;
}

/** The eye never goes below this height over the land. */
export const EYE_HEIGHT = 2.2;
/** The closest the eye stands to what it looks at, metres. */
export const R_MIN = 6;
/** Elevation of the eye over the target's ground plane, radians (framings). */
const EL_MIN = 0.08, EL_MAX = 1.45;
/** The gaze itself: down to almost straight down, up to well over the horizon. */
const PITCH_MIN = -1.5, PITCH_MAX = 1.25;
/** Seconds for the float to settle onto the ground's smoothed height, and for a clearance lift. */
const FLOAT_SMOOTH = 0.6, LIFT_SMOOTH = 0.28;
/** Radians of orbit per pixel of drag — the grab-the-world gesture. */
const ORBIT_SENS = 0.0044;
/** Radians of elevation per pixel of vertical drag. */
const ELEV_SENS = 0.0036;
/** Metres of pan per pixel of pan-drag, as a share of the distance. */
const PAN_SENS = 0.0022;
/** One wheel notch multiplies the distance by this. */
const ZOOM_STEP = 1.11;
/**
 * Seconds for the orbit to absorb drag input, and for the distance to reach
 * a zoom. Two frames of lag the hand cannot feel, and the eye has weight
 * instead of jitter — which matters because the pick runs off wherever the
 * cursor is over whatever the eye settles on.
 */
const LOOK_SMOOTH = 0.03;
const ZOOM_SMOOTH = 0.11;
/** One wheel notch multiplies a chosen focus radius by this — `stepFocus`. */
const FOCUS_STEP = 1.14;

/** Wrap an angle into (−π, π] so eased yaw always turns the short way. */
const wrapAngle = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

export class DeityPresence {
  /* ── the eye, derived every step ─────────────────────────────────────── */
  /** Position in world metres; y is absolute (sea level = 0). */
  x = 0; y = 200; z = 0;
  /** Gaze. yaw 0 looks toward −z; positive pitch looks up. */
  yaw = 0; pitch = -0.4;
  /** The eye's velocity, m/s — read by the sound and the drift, never set by them. */
  vx = 0; vy = 0; vz = 0;

  /* ── the orbit ─────────────────────────────────────────────────────── */
  /** The point the eye stands off and looks at. `ty` follows the ground unless held. */
  tx = 0; ty = 0; tz = 0;
  /** Distance to the target, metres; the wheel moves `rWant` and `r` follows. */
  r = 340;
  private rWant = 340;
  /** Azimuth about the target (yaw of the eye's offset) and elevation over its ground plane. */
  az = -0.7; el = 0.42;

  /** Reach: how far the eye may stand off the land — caps `r`. */
  maxClearance = 60000;
  /**
   * How fast this god carries its target across the land, and how much of that
   * the distance grants — `presence.speed` and `presence.speed.fromAltitude`,
   * both multipliers, both set by `main.ts` in `syncReach` beside
   * `maxClearance`. Two purchases: a god that simply moves faster, and a god
   * the height carries — quick from far out, ordinary close in.
   */
  speed = 1;
  speedFromAltitude = 1;
  /** Called on every wheel notch, so the chrome can say the scale is being steered. */
  onZoom?: (notches: number) => void;

  /**
   * WHO OWNS THE INPUT (2026-08-19, the mortal skeleton). While the player is
   * in a mortal body (`ui/mortalPresence.ts`) the deity is DORMANT: its
   * listeners drop every key, button and wheel notch, and `step` leaves the
   * eye exactly where it stands — so W walks the mortal and never also
   * carries the god. Nothing here knows what a mortal is; `ui/inhabit.ts` is
   * the one place that knows which body is the player's, and it flips this
   * and calls `clearInput` on the way across. State is KEPT, not reset:
   * ascending finds the eye, the orbit, the zoom and the gaze where they were.
   */
  active = true;

  /**
   * The floor the eye may not sink below, metres over the ground. `EYE_HEIGHT`
   * for the god — a deity that can put its eye in the dirt is a deity that can
   * see the terrain's own construction, and every clearance rule in `step`
   * reads this.
   *
   * It is a field rather than the constant it defaults to for exactly one
   * caller: `src/workshop/` stands the same body in a white room whose
   * specimens are a mushroom five centimetres tall, where a 2.2 m floor is the
   * difference between inspecting a thing and looking down at it. Nothing in
   * the game sets it, so the game's eye is bit-identical to before.
   */
  minHeight = EYE_HEIGHT;

  /**
   * The closest the wheel may dolly the eye to what it looks at, metres.
   * `R_MIN` for the god, and for the same reason as `minHeight`: `src/workshop/`
   * looks at specimens five centimetres tall, where a six-metre floor on the
   * dolly means the wheel does nothing at all. Only the wheel reads it —
   * walking has always been free to put the eye wherever the hand takes it.
   */
  minRange = R_MIN;

  readonly input: FlightInput = { forward: 0, strafe: 0, lift: 0, surge: false };

  private wired = false;
  private keys = new Set<string>();
  /** The pointer drag in progress: orbiting the eye, or panning the target. */
  private drag: { mode: 'orbit' | 'pan'; x: number; y: number } | null = null;
  private dragPx = 0;
  private lastDragPx = 0;
  private touches = new Map<number, { x: number; y: number }>();
  private pinch: { d: number; r: number } | null = null;
  /** Orbit input awaiting the smoothing drain in `step`. Radians. */
  private pendAz = 0;
  private pendEl = 0;
  /** Pan input awaiting `step`, in metres along the eye's ground axes. */
  private pendPanF = 0;
  private pendPanS = 0;
  /** Seconds since the hand last orbited or panned. Opens far from zero: a
   *  fresh presence is not steering, so a glide may take the eye at once. */
  private lookIdleS = 999;
  /** True for the frame in which somebody held the target's height. */
  private heldY = false;
  /**
   * THE EYE IS PRIMARY (2026-08-18, Griffin's direction: the camera is
   * independent, looks up, and is not stuck tracking a point on the ground).
   * These carry the player's own body through a frame; the orbit fields above
   * are DERIVED from them whenever the hand moves the eye, and only a hold, a
   * framing or a zoom drives them the other way.
   */
  /** How high the player chooses to float over the ground under them, metres. */
  private hCruise = 30;
  /** The ground under the eye, smoothed — what the float rides on. */
  private gSmooth = 0;
  /** Distance the gaze last landed at, for when it lands on nothing (the sky). */
  private rGaze = 100;
  /** A zoom is on its way: the wheel moved `rWant` and the eye should dolly. */
  private zoomPending = false;
  /**
   * The wheel dollies only while something is chosen (Griffin): with no
   * subject a god does not "zoom" — it walks or drops. `main.ts` sets it.
   */
  zoomEnabled = false;

  constructor(el?: HTMLElement) {
    if (el) this.wire(el);
    this.derive();
    this.heldY = true;
  }

  /** True while the pointer is down and has moved the view. */
  get dragging(): boolean {
    return this.drag !== null && this.dragPx > 6;
  }

  /**
   * Did the pointer gesture that just ended move the view? The click event
   * fires after every drag, and a god turning the eye about a herd must not
   * cast the armed verb on whatever the release happened to be over.
   */
  wasLookingAround(): boolean {
    return this.lastDragPx > 6;
  }

  /**
   * Is the hand on the eye right now — orbiting or panning? A glide (the
   * arrival, a chosen subject) must not wrestle the mouse; the hand always
   * wins, and keeps winning for a beat after it stops so the glide re-takes
   * the eye gently.
   */
  get steering(): boolean {
    return this.lookIdleS < 0.4;
  }

  /**
   * IS THE HAND MOVING THE TARGET? A pan key down, or a pan drag with real
   * travel in it. This is what drops a hold: orbiting and zooming keep the
   * subject; carrying the target away is leaving it. `main.ts` reads it.
   */
  get moving(): boolean {
    return this.input.forward !== 0 || this.input.strafe !== 0
      || (this.drag?.mode === 'pan' && this.dragPx > 6);
  }

  /** The unit gaze direction. */
  lookDir(): { x: number; y: number; z: number } {
    const cp = Math.cos(this.pitch);
    return { x: -Math.sin(this.yaw) * cp, y: Math.sin(this.pitch), z: -Math.cos(this.yaw) * cp };
  }

  /* ── placing the eye ─────────────────────────────────────────────────── */

  /**
   * Look at a world point from where the eye already is: the target moves to
   * the point and the orbit is re-derived so the eye does not. A snap means
   * it — queued orbit input is dropped.
   */
  aimAt(tx: number, ty: number, tz: number): void {
    this.tx = tx; this.ty = ty; this.tz = tz;
    this.gazeAtTarget();
    this.orbitFromEye();
    this.rGaze = this.r;
    this.pendAz = 0; this.pendEl = 0;
    this.heldY = true;
  }

  /**
   * Put the eye at `from`, looking at `at` — the one call the arrival, the
   * capture stations and the opening standpoint need. Both are world points.
   */
  placeEye(from: { x: number; y: number; z: number }, at: { x: number; y: number; z: number }): void {
    this.x = from.x; this.y = from.y; this.z = from.z;
    this.tx = at.x; this.ty = at.y; this.tz = at.z;
    this.gazeAtTarget();
    this.orbitFromEye();
    this.rGaze = this.r;
    // the float learns its height from here on the next step (heldY skips a
    // frame of ground-following, and `step` re-reads hCruise off the eye)
    this.heldY = true;
  }

  /** Ease the target toward a world point while the eye stays put — the glide's gaze, never the hand's. */
  easeLookToward(tx: number, ty: number, tz: number, dt: number, tau = 0.35): void {
    const k = 1 - Math.exp(-dt / tau);
    this.tx += (tx - this.tx) * k;
    this.ty += (ty - this.ty) * k;
    this.tz += (tz - this.tz) * k;
    this.gazeAtTarget();
    this.orbitFromEye();
    this.heldY = true;
  }

  /**
   * HOLD THE TARGET ON A SUBJECT — the follow. The target eases onto the point
   * (`tau` seconds; 0 snaps) and its height is the subject's, not the ground's,
   * for this frame. The eye keeps its distance and its angles, so it travels
   * with what it watches and the hand may still orbit and zoom about it.
   */
  hold(x: number, y: number, z: number, dt = 0, tau = 0): void {
    const k = tau > 0 ? 1 - Math.exp(-dt / tau) : 1;
    const dx = (x - this.tx) * k, dy = (y - this.ty) * k, dz = (z - this.tz) * k;
    this.tx += dx; this.ty += dy; this.tz += dz;
    // the eye keeps its distance and its angles: it goes where the subject
    // goes, and looks at it — a translation, so what the hand set stays set
    this.x += dx; this.y += dy; this.z += dz;
    this.heldY = true;
    this.gazeAtTarget();
    this.orbitFromEye();
  }

  /**
   * Ease the distance and the elevation toward a framing — how far off, and how
   * high, this kind of subject is worth watching from (`ui/observe.ts`). The
   * azimuth is left alone: the eye keeps the side it is already on, which is
   * most of what makes choosing something read as attention moving rather than
   * as a cut. Returns true once it has arrived.
   */
  frameToward(distance: number, elevation: number, dt: number, tau = 1.5): boolean {
    const k = 1 - Math.exp(-dt / tau);
    const rWant = clamp(distance, R_MIN, this.maxClearance);
    this.rWant += (rWant - this.rWant) * k;
    this.r = this.rWant;
    this.el = clamp(this.el + (clamp(elevation, EL_MIN, EL_MAX) - this.el) * k, EL_MIN, EL_MAX);
    this.derive();
    this.heldY = true;
    return Math.abs(this.rWant - rWant) < Math.max(1, rWant * 0.03) && Math.abs(this.el - elevation) < 0.03;
  }

  /** Bring the eye to this distance, at once (`__terra.setFocus`, tests). */
  zoomTo(distance: number): void {
    this.rWant = clamp(distance, R_MIN, this.maxClearance);
    this.r = this.rWant;
    this.derive();
    this.heldY = true;
  }

  /* ── one frame ───────────────────────────────────────────────────────── */

  /**
   * Drop everything the hand and the keys were doing: held keys, a drag in
   * progress, queued look and pan, a zoom on its way. Called when input
   * ownership changes hands, so a W held at the moment of a transition does
   * not carry the eye away on the far side of it. The eye itself is not moved.
   */
  clearInput(): void {
    this.keys.clear();
    this.drag = null;
    this.dragPx = 0;
    this.lastDragPx = 0;
    this.touches.clear();
    this.pinch = null;
    this.pendAz = 0; this.pendEl = 0;
    this.pendPanF = 0; this.pendPanS = 0;
    this.zoomPending = false;
    this.rWant = this.r;
    this.input.forward = 0; this.input.strafe = 0; this.input.lift = 0;
    this.input.surge = false;
  }

  /** One frame. `groundAt` is the terrain; the sea floor is 0. A dormant eye does not move. */
  step(dt: number, groundAt: (x: number, z: number) => number): void {
    if (!this.active) return;
    if (this.wired) this.syncInput();
    this.lookIdleS += dt;
    const ex = this.x, ey = this.y, ez = this.z;
    const g = (x: number, z: number) => Math.max(groundAt(x, z), 0);

    // Drain the pending look through the smoothing window. The remainder is
    // carried, not lost, so the gaze always arrives exactly where the hand
    // sent it — the smoothing shapes *when*, never *where*.
    if (this.pendAz !== 0 || this.pendEl !== 0) {
      const k = 1 - Math.exp(-dt / LOOK_SMOOTH);
      let da = this.pendAz * k, de = this.pendEl * k;
      this.pendAz -= da; this.pendEl -= de;
      if (Math.abs(this.pendAz) < 2e-5) { da += this.pendAz; this.pendAz = 0; }
      if (Math.abs(this.pendEl) < 2e-5) { de += this.pendEl; this.pendEl = 0; }
      // THE DRAG TURNS THE HEAD, NOT THE WORLD (2026-08-18, Griffin's call):
      // the eye stays where the player is and the gaze turns — up past the
      // horizon if the hand says so — and the target is re-found where the
      // new gaze lands. Same hand as before: right turns right, down looks
      // down. A drag while a subject is held is the player looking away, and
      // `main.ts` reads `steering` to let the follow go.
      this.yaw = wrapAngle(this.yaw + da);
      this.pitch = clamp(this.pitch - de, PITCH_MIN, PITCH_MAX);
    }
    // Space lifts the player and C (or Ctrl) lowers them (2026-08-18): the
    // float's own height changes, at a speed granted by distance as the walk
    // is; the gaze is kept.
    if (this.input.lift !== 0) {
      const vs = clamp(4 + this.r * 0.30 * this.speedFromAltitude, 4, this.maxClearance * 0.6)
        * this.speed * (this.input.surge ? 3 : 1);
      this.hCruise = clamp(this.hCruise + this.input.lift * vs * dt, this.minHeight, this.maxClearance);
    }

    // The player crosses the land. Speed is granted by distance — close in it
    // is a walk, from the ceiling it is kilometres a second — with a floor so
    // ground level is a walk and not a crawl, capped by reach, surged by shift.
    // Forward is where the gaze points across the ground; strafe is its right.
    const inp = this.input;
    if (inp.forward !== 0 || inp.strafe !== 0) {
      const l = Math.hypot(inp.forward, inp.strafe) || 1;
      const spd = Math.min(
        clamp(12 + this.r * 0.55 * this.speedFromAltitude, 12, this.maxClearance * 1.2)
          * this.speed * (inp.surge ? 3 : 1),
        30000) * dt;
      this.pendPanF += (inp.forward / l) * spd;
      this.pendPanS += (inp.strafe / l) * spd;
    }
    const moved = this.pendPanF !== 0 || this.pendPanS !== 0;
    if (moved) {
      // forward is −(sin yaw, cos yaw): where the gaze points across the ground
      const sy = Math.sin(this.yaw), cy = Math.cos(this.yaw);
      this.x += -sy * this.pendPanF + cy * this.pendPanS;
      this.z += -cy * this.pendPanF - sy * this.pendPanS;
      this.pendPanF = 0; this.pendPanS = 0;
    }

    // THE FLOAT (2026-08-18, "a deity floating through the terrain"). The eye
    // rides at its chosen height over the ground under it — over a SMOOTHED
    // ground, so a rise or a gully passes under as a swell rather than a step
    // — and is lifted, gently, clear of anything the sight line would cut
    // through. Nothing here snaps: the old rule added the whole clearance in
    // one frame and took it away in the next, which was the bump.
    if (this.heldY) {
      // held on a subject this frame: the target moved and the eye went with it
      // (`hold`); the float re-learns its height from wherever that left it —
      // never inside the hill, whatever placed it
      this.gSmooth = g(this.x, this.z);
      this.y = Math.max(this.y, this.gSmooth + this.minHeight);
      this.hCruise = clamp(this.y - this.gSmooth, this.minHeight, this.maxClearance);
    } else {
      const gNow = g(this.x, this.z);
      const kf = 1 - Math.exp(-dt / FLOAT_SMOOTH);
      this.gSmooth += (gNow - this.gSmooth) * kf;
      if (Math.abs(gNow - this.gSmooth) < 0.02) this.gSmooth = gNow;
      let wantY = this.gSmooth + this.hCruise;
      // …and clear of the hill: eye height over the true ground here, and over
      // any rise between the eye and what it looks at (a lift of Δ at
      // parameter t along the sight line costs Δ/(1−t) at the eye)
      let need = gNow + this.minHeight;
      if (this.pitch < 0) {
        for (const t of [0.15, 0.35, 0.55, 0.75]) {
          const sx = this.x + (this.tx - this.x) * t, sz = this.z + (this.tz - this.z) * t;
          const ray = wantY + (this.ty - wantY) * t;
          const rise = g(sx, sz) + this.minHeight - ray;
          if (rise > 0) need = Math.max(need, wantY + rise / (1 - t));
        }
      }
      wantY = Math.max(wantY, need);
      const kl = 1 - Math.exp(-dt / (wantY > this.y ? LIFT_SMOOTH : FLOAT_SMOOTH));
      this.y += (wantY - this.y) * kl;
      if (Math.abs(wantY - this.y) < 0.01) this.y = wantY;
      // never inside the hill whatever the easing is doing
      this.y = Math.max(this.y, gNow + this.minHeight);
    }

    // The target: where the gaze lands, unless a hold put it somewhere. Then
    // the orbit fields — the distance that is the scale of attention, the
    // azimuth and elevation a framing speaks in — follow from eye and target.
    if (!this.heldY) this.landGaze(g);
    this.heldY = false;

    // the wheel: a dolly along the gaze toward what is chosen, and nothing
    // otherwise. Eased, inside reach.
    if (this.zoomPending) {
      this.rWant = clamp(this.rWant, R_MIN, this.maxClearance);
      const kz = 1 - Math.exp(-dt / ZOOM_SMOOTH);
      this.r += (this.rWant - this.r) * kz;
      if (Math.abs(this.rWant - this.r) < 0.01) { this.r = this.rWant; this.zoomPending = false; }
      this.r = clamp(this.r, R_MIN, this.maxClearance);
      const d = this.lookDir();
      this.x = this.tx - d.x * this.r; this.y = this.ty - d.y * this.r; this.z = this.tz - d.z * this.r;
      this.y = Math.max(this.y, g(this.x, this.z) + this.minHeight);
      this.gSmooth = g(this.x, this.z);
      this.hCruise = clamp(this.y - this.gSmooth, this.minHeight, this.maxClearance);
      this.orbitFromEye();
    } else {
      this.rWant = this.r;
    }

    if (dt > 0) {
      this.vx = (this.x - ex) / dt; this.vy = (this.y - ey) / dt; this.vz = (this.z - ez) / dt;
    }
  }

  /* ── the arithmetic ──────────────────────────────────────────────────── */

  /**
   * Put the target where the gaze lands: on the ground it meets, if it meets
   * ground inside reach — over a valley falling away that may be kilometres
   * off, and then the distance, and with it the scale of attention, is honestly
   * that — or, when it meets nothing (the sky, or past reach), at the distance
   * it last landed at, hanging in the air. Coarse steps growing with distance,
   * then a bisection on the first crossing; `g` is the analytic land with the
   * sea at 0, so a gaze over water lands on the water. Then the orbit fields
   * follow from eye and target.
   */
  private landGaze(g: (x: number, z: number) => number): void {
    const ex = this.x, ey = this.y, ez = this.z;
    const d = this.lookDir();
    const far = this.maxClearance;
    let t0 = 0, t1 = -1;
    if (d.y < 0.02) {
      for (let t = 2; t <= far; t *= 1.22) {
        if (ey + d.y * t < g(ex + d.x * t, ez + d.z * t)) { t1 = t; break; }
        t0 = t;
      }
    }
    let t: number;
    if (t1 < 0) t = clamp(this.rGaze, R_MIN, far);
    else {
      // 26 halvings of a bracket a fifth of the distance wide is a landing to
      // the micrometre at reach — the distance IS the scale of attention, and
      // a landing a metre off would breathe the focus while the eye stood still
      for (let i = 0; i < 26; i++) {
        const tm = (t0 + t1) * 0.5;
        if (ey + d.y * tm < g(ex + d.x * tm, ez + d.z * tm)) t1 = tm; else t0 = tm;
      }
      t = clamp((t0 + t1) * 0.5, R_MIN, far);
      this.rGaze = t;
    }
    this.tx = ex + d.x * t; this.ty = ey + d.y * t; this.tz = ez + d.z * t;
    this.orbitFromEye();
  }

  /** Eye and gaze from the orbit — a framing, a zoom, the harness. */
  private derive(): void {
    const ce = Math.cos(this.el), se = Math.sin(this.el);
    this.x = this.tx + Math.sin(this.az) * ce * this.r;
    this.y = this.ty + se * this.r;
    this.z = this.tz + Math.cos(this.az) * ce * this.r;
    this.gazeAtTarget();
    this.rGaze = this.r;
  }

  private gazeAtTarget(): void {
    const dx = this.tx - this.x, dy = this.ty - this.y, dz = this.tz - this.z;
    this.yaw = Math.atan2(-dx, -dz);
    this.pitch = clamp(Math.atan2(dy, Math.max(1e-6, Math.hypot(dx, dz))), -1.5, 1.5);
  }

  /**
   * The orbit from where the eye is and what it looks at. The elevation is
   * left as it is — negative when the eye looks UP at its target (a bird, a
   * crown, the sky) — because it is a reading now, not a constraint; the
   * framings that drive the orbit the other way (`frameToward`) still clamp
   * to [EL_MIN, EL_MAX] themselves.
   */
  private orbitFromEye(): void {
    const dx = this.x - this.tx, dy = this.y - this.ty, dz = this.z - this.tz;
    const flat = Math.hypot(dx, dz);
    this.r = clamp(Math.hypot(dx, dy, dz), R_MIN, this.maxClearance);
    if (!this.zoomPending) this.rWant = this.r;
    this.az = Math.atan2(dx, dz);
    this.el = clamp(Math.atan2(dy, Math.max(1e-6, flat)), -EL_MAX, EL_MAX);
  }

  private syncInput(): void {
    const k = this.keys;
    this.input.forward =
      (k.has('w') || k.has('arrowup') ? 1 : 0) - (k.has('s') || k.has('arrowdown') ? 1 : 0);
    this.input.strafe =
      (k.has('d') || k.has('arrowright') ? 1 : 0) - (k.has('a') || k.has('arrowleft') ? 1 : 0);
    // Ctrl descends too. Mind that Ctrl+W is the browser's own close-tab in
    // Chrome and cannot be prevented from a page: C is the safe key to hold
    // while walking forward, and Ctrl is there because Griffin asked for it.
    this.input.lift =
      (k.has(' ') ? 1 : 0) - (k.has('c') || k.has('control') ? 1 : 0);
    this.input.surge = k.has('shift');
  }

  /* ── input ───────────────────────────────────────────────────────────── */

  private wire(el: HTMLElement): void {
    this.wired = true;
    // the right button pans, so the browser's menu must not answer it
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    el.addEventListener('pointerdown', (e) => {
      if (!this.active) return;
      if (e.pointerType === 'touch') {
        this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this.touches.size === 2) {
          const [a, b] = [...this.touches.values()];
          this.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), r: this.rWant };
          this.drag = null;
          return;
        }
      }
      // left orbits; right or middle carries the target
      const mode = e.pointerType !== 'touch' && (e.button === 2 || e.button === 1) ? 'pan' : 'orbit';
      this.drag = { mode, x: e.clientX, y: e.clientY };
      this.dragPx = 0;
      // best-effort: a pointer that already left (synthetic clicks) cannot be
      // captured, and that is fine — capture only serves drag continuity off
      // the element's edge
      try { el.setPointerCapture(e.pointerId); } catch { /* released already */ }
      e.preventDefault();
    });
    el.addEventListener('pointermove', (e) => {
      if (!this.active) return;
      if (e.pointerType === 'touch' && this.touches.has(e.pointerId)) {
        this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }
      // pinch is the touch wheel: spreading the fingers brings the eye in
      if (this.pinch && this.touches.size === 2) {
        const [a, b] = [...this.touches.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.zoomEnabled) {
          this.rWant = clamp(this.pinch.r * Math.pow(this.pinch.d / Math.max(8, d), 1.25),
                             R_MIN, this.maxClearance);
          this.zoomPending = true;
          this.onZoom?.(0);
        }
        return;
      }
      if (!this.drag) return;
      const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
      this.dragPx += Math.abs(dx) + Math.abs(dy);
      if (this.drag.mode === 'orbit') {
        // grab the world: drag right swings the ground right (the eye goes
        // left round the target); drag down tips the ground toward you (the
        // eye rises and looks down onto it) — the old camera's own hand
        this.pendAz -= dx * ORBIT_SENS;
        this.pendEl += dy * ELEV_SENS;
      } else {
        // grab the world: dragging right carries the ground right, so the
        // target goes left; dragging down brings the ground toward the eye, so
        // the target comes back — scaled by how far out the eye stands
        const m = this.r * PAN_SENS;
        this.pendPanS -= dx * m;
        this.pendPanF -= dy * m;
      }
      this.lookIdleS = 0;
      this.drag = { ...this.drag, x: e.clientX, y: e.clientY };
    });
    const end = (e: PointerEvent) => {
      if (!this.active) return;
      this.touches.delete(e.pointerId);
      if (this.touches.size < 2) this.pinch = null;
      if (this.drag) this.lastDragPx = this.dragPx;
      this.drag = null;
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    // the wheel is the distance to what is CHOSEN — a dolly toward it or
    // away — and, through it, the scale of attention. With nothing chosen it
    // does nothing (Griffin, 2026-08-18): a god walks and drops; it does not zoom the world.
    el.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (!this.active || !this.zoomEnabled) return;
      const notches = Math.sign(e.deltaY);
      this.rWant = clamp(this.rWant * Math.pow(ZOOM_STEP, notches), this.minRange, this.maxClearance);
      this.zoomPending = true;
      this.onZoom?.(notches);
    }, { passive: false });
    addEventListener('keydown', (e) => {
      if (!this.active) return;
      const k = e.key.toLowerCase();
      this.keys.add(k);
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright',
           'w', 'a', 's', 'd', ' ', 'c', 'control'].includes(k)) e.preventDefault();
    });
    addEventListener('keyup', (e) => { if (this.active) this.keys.delete(e.key.toLowerCase()); });
    // alt-tab must not leave a key stuck down and the target sliding for ever
    addEventListener('blur', () => this.keys.clear());
  }
}

/**
 * How much country the presence is attending to, in metres, at this height.
 *
 * This is what the Chronicle notices: a god learns what happens where it
 * is, not everything that happens anywhere. Six kilometres is the floor —
 * a whole valley, so hovering over a camp never misses the camp's own life
 * — and it opens toward the region as the god rises. The ceiling is the
 * island; beyond that the number would be a lie dressed as omniscience.
 */
export const awarenessRange = (altitude: number): number =>
  clamp(altitude * 6, 6000, 40000);

/**
 * One notch's worth of a CHOSEN focus radius — kept for the dev handle and
 * the harness (`__terra.setFocus`), which speak in cast radii. Multiplicative,
 * clamped between the smallest presence and what reach permits, and jumping
 * the deliberate gap in the band table (Valley ends at 1500 m, Watershed
 * begins at 2000) in the direction it was turning.
 */
export function stepFocus(radius: number, steps: number, cap: number): number {
  const min = FOCUS_BANDS[0].cast[0];
  let r = clamp(radius * Math.pow(FOCUS_STEP, steps), min, Math.max(min, cap));
  for (let i = 1; i < FOCUS_BANDS.length; i++) {
    const below = FOCUS_BANDS[i - 1].cast[1], above = FOCUS_BANDS[i].cast[0];
    if (r > below && r < above) {
      r = steps > 0 && cap >= above ? above : below;
      break;
    }
  }
  return r;
}

/**
 * The eye distance that pays a given focus radius — `focusFor` run backwards,
 * band by band. What `__terra.setFocus(radius)` moves the eye to, so the
 * harness can still ask for "the local register" and get the distance that
 * means it. A radius in the gap between two bands resolves to the nearer edge.
 */
export function camForFocus(radius: number): number {
  const bands = FOCUS_BANDS;
  let best = bands[0], bestGap = Infinity;
  for (const b of bands) {
    const gap = radius < b.cast[0] ? b.cast[0] - radius : radius > b.cast[1] ? radius - b.cast[1] : 0;
    if (gap < bestGap) { bestGap = gap; best = b; }
  }
  const r = clamp(radius, best.cast[0], best.cast[1]);
  const t = Math.log(r / best.cast[0]) / Math.log(best.cast[1] / best.cast[0]);
  return best.cam[0] * Math.pow(best.cam[1] / best.cam[0], t);
}
